// FileExplorer.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { Loader, Folder, File, Edit, Download, Trash2, Code, ClipboardPaste, UploadCloud, SquareTerminal, Clock, Eye, EyeOff, Brush, Pin, PinOff } from 'lucide-react';
import ContextMenu from './ContextMenu';
import { motion, AnimatePresence } from 'framer-motion';
import { Tree } from './FileTree';

/* ========================
 * FileExplorer - Main component for browsing and managing workspace files
 * ========================*/
const FileExplorer = ({ files, isLoading, error, onOpenFile, onDownloadFile, onInitiateRename, onInitiateDeleteFile, onCopyForVSCode, onUploadZip, onOpenTerminal, onInitiateCreateFile, onInitiateCreateFolder, expandedFolders, onToggleFolder, onInitiateRefactor, activeFilePath, pinnedFilePaths = new Set(), onToggleFilePin, isBrainEnabled = false }) => {
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, targetNode: null });
    const [sortOrder, setSortOrder] = useState('default'); // 'default' or 'time'
    const [showHiddenFiles, setShowHiddenFiles] = useState(false);
    
    const toggleSortOrder = useCallback(() => {
        setSortOrder(prev => (prev === 'default' ? 'time' : 'default'));
    }, []);

    const toggleShowHiddenFiles = useCallback(() => {
        setShowHiddenFiles(prev => !prev);
    }, []);

    const handleContextMenu = (x, y, node) => {
        setContextMenu({ show: true, x, y, targetNode: node });
    };

    const closeContextMenu = () => {
        setContextMenu({ show: false, x: 0, y: 0, targetNode: null });
    };

    const handleCreateFolder = () => {
        onInitiateCreateFolder();
    };

    const handleCreateFile = () => {
        onInitiateCreateFile();
    };

    const contextMenuActions = useMemo(() => {
        if (!contextMenu.targetNode) return [];

        const fileName = contextMenu.targetNode.name || '';
        const isPinned = contextMenu.targetNode.type === 'file' && pinnedFilePaths.has(contextMenu.targetNode.path);
        const canCssCleanup = /\.css$/i.test(fileName);

        const allPossibleActions = [
            // File-specific actions
            { label: 'Open in Editor', icon: <Code size={14} />, onClick: onOpenFile, type: 'file' },
            
            // CSS Cleanup Refactor
            ...(canCssCleanup ? [{ label: 'CSS Cleanup', icon: <Brush size={14} />, onClick: (node) => onInitiateRefactor(node, 'REFACTOR_CSS_CLEANUP'), type: 'file' }] : []),

            { label: 'Copy to Clipboard', icon: <ClipboardPaste size={14} />, onClick: onCopyForVSCode, type: 'file' },
            { label: 'Download', icon: <Download size={14} />, onClick: onDownloadFile, type: 'file' },
            ...(onToggleFilePin ? [
                { separator: true, type: 'file' },
                { label: isPinned ? 'Unpin' : 'Pin to Every Message', icon: isPinned ? <PinOff size={14} /> : <Pin size={14} />, onClick: onToggleFilePin, type: 'file' }
            ] : []),

            // Folder-specific actions
            { label: 'Open in Terminal', icon: <SquareTerminal size={14} />, onClick: onOpenTerminal, type: 'directory' },
            
            // Common actions
            { separator: true, type: 'both' },
            { label: 'Rename', icon: <Edit size={14} />, onClick: onInitiateRename, type: 'both' },
            { separator: true, type: 'both' },
            { label: 'Delete', icon: <Trash2 size={14} className="text-red-500/80" />, onClick: onInitiateDeleteFile, type: 'both' },
        ];
        
        const typeFilteredActions = allPossibleActions.filter(action => 
            action.type === 'both' || action.type === contextMenu.targetNode.type
        );

        return typeFilteredActions.filter((action, index, arr) => {
            if (!action.separator) return true;
            const isFirst = index === 0;
            const isLast = index === arr.length - 1;
            const isNextToSeparator = arr[index + 1]?.separator;
            return !isFirst && !isLast && !isNextToSeparator;
        });

    }, [contextMenu.targetNode, onOpenFile, onCopyForVSCode, onInitiateRename, onDownloadFile, onInitiateDeleteFile, onOpenTerminal, onInitiateRefactor, onToggleFilePin, pinnedFilePaths]);

    if (isLoading) {
        return <div className="h-full flex items-center justify-center text-on-surface-dim"><Loader className="animate-spin mr-2" size={16} />Loading files...</div>;
    }

    if (error && error !== 'Not Found') {
        return <div className="h-full flex items-center justify-center p-4 text-center"><span className="text-[11px] text-on-surface-dim opacity-60">Connection issues. Check your connection.</span></div>;
    }

    return (
        <div className="h-full flex flex-col relative">
            <div className="p-2 border-b border-border-color flex-shrink-0 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={handleCreateFile} className="text-[11px] text-on-surface-dim hover:text-on-surface hover:opacity-100 opacity-80 transition-opacity" title="New File">+ New File</button>
                    <button onClick={handleCreateFolder} className="text-[11px] text-on-surface-dim hover:text-on-surface hover:opacity-100 opacity-80 transition-opacity" title="New Folder">+ New Folder</button>
                    <button onClick={onUploadZip} className="text-[11px] text-on-surface-dim hover:text-on-surface hover:opacity-100 opacity-80 transition-opacity" title="Upload & Unzip a .zip archive">⇧ Upload Zip</button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={toggleShowHiddenFiles}
                        className={`p-1 rounded-md transition-all ${
                            showHiddenFiles
                                ? 'bg-surface-dark text-on-surface'
                                : 'text-on-surface-dim hover:text-on-surface hover:bg-surface-dark'
                        }`}
                        title={showHiddenFiles ? "Hide hidden files" : "Show hidden files"}>
                        {showHiddenFiles ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                     <button 
                        onClick={toggleSortOrder} 
                        className="flex items-center gap-1.5 p-1 rounded-md text-on-surface-dim hover:text-on-surface hover:bg-surface-dark transition-all" 
                        title={sortOrder === 'default' ? 'Sort by modification time' : 'Sort alphabetically'}>
                        <Clock size={14} />
                    </button>
                    <button onClick={() => onOpenTerminal(null)} className="flex items-center gap-1.5 p-1 rounded-md text-on-surface-dim hover:text-on-surface hover:bg-surface-dark transition-all" title="Open Terminal in Root">
                        <SquareTerminal size={14} />
                    </button>
                </div>
            </div>
            <div
                className="flex-1 overflow-y-auto custom-scrollbar"
                onClick={closeContextMenu}
            >
                {Object.keys(files).length > 0 ? (
                    <Tree nodes={files} level={0} onOpenFile={onOpenFile} expandedFolders={expandedFolders} onToggleFolder={onToggleFolder} onContextMenu={handleContextMenu} sortOrder={sortOrder} showHiddenFiles={showHiddenFiles} activeFilePath={activeFilePath} pinnedFilePaths={pinnedFilePaths} isBrainEnabled={isBrainEnabled} />
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center p-4">
                         <p className="text-sm text-on-surface-dim">Workspace is empty.</p>
                         <p className="text-xs text-on-surface-dim mt-1">Drag and drop files here to add them.</p>
                    </div>
                )}
            </div>
            <ContextMenu {...contextMenu} onClose={closeContextMenu} actions={contextMenuActions} targetNode={contextMenu.targetNode} />
        </div>
    );
};

export default FileExplorer;